/**
 * Business Hours Utility Functions
 * Handles cafe opening hours logic and formatting
 */

/**
 * Check if cafe is currently open based on business hours
 * @param {Array} businessHours - Array of {day, openTime, closeTime} objects
 * @returns {boolean} - True if cafe is currently open
 */
// eslint-disable-next-line no-unused-vars
export function isCurrentlyOpen(businessHours, _timezone) {
  if (
    !businessHours ||
    !Array.isArray(businessHours) ||
    businessHours.length === 0
  ) {
    return false;
  }

  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;

  // First check if we're in the overnight period from previous day
  const previousDay = currentDay === 0 ? 6 : currentDay - 1;
  const yesterdayHours = businessHours.find((h) => h.day === previousDay);

  if (yesterdayHours) {
    const prevOpenTime = parseTimeToMinutes(yesterdayHours.openTime);
    const prevCloseTime = parseTimeToMinutes(yesterdayHours.closeTime);

    // Previous day closes past midnight (overnight)
    if (prevCloseTime < prevOpenTime) {
      // We're in the early morning hours - check if still within yesterday's closing time
      if (currentTimeInMinutes < prevCloseTime) {
        return true;
      }
    }
  }

  // Find hours for current day
  const todayHours = businessHours.find((h) => h.day === currentDay);

  if (!todayHours) {
    return false; // Closed on this day
  }

  // Parse open and close times
  const openTime = parseTimeToMinutes(todayHours.openTime);
  const closeTime = parseTimeToMinutes(todayHours.closeTime);

  // Handle today's hours that close past midnight
  if (closeTime < openTime) {
    // Today opens and closes next day - check if we're after opening time
    return currentTimeInMinutes >= openTime;
  }

  // Normal case: open and close on same day
  return currentTimeInMinutes >= openTime && currentTimeInMinutes < closeTime;
}

/**
 * Get business hours for current day
 * @param {Array} businessHours - Array of {day, openTime, closeTime} objects
 * @param {string} timezone - Optional timezone parameter for future timezone support
 * @returns {Object|null} - Today's hours or null if closed
 */
// eslint-disable-next-line no-unused-vars
export function getCurrentDayHours(businessHours, _timezone) {
  if (!businessHours || !Array.isArray(businessHours)) {
    return null;
  }

  const currentDay = new Date().getDay();
  return businessHours.find((h) => h.day === currentDay) || null;
}

/**
 * Format business hours into human-readable text - shows only today's hours
 * @param {Array} businessHours - Array of {day, openTime, closeTime} objects
 * @returns {string} - Formatted hours text for today (e.g., "9AM-5PM" or "Closed today")
 */
export function formatBusinessHours(businessHours) {
  if (
    !businessHours ||
    !Array.isArray(businessHours) ||
    businessHours.length === 0
  ) {
    return "Closed today";
  }

  const currentDay = new Date().getDay();

  // Get today's hours
  const todayHours = businessHours.find((h) => h.day === currentDay);

  if (!todayHours) {
    return "Closed today";
  }

  const timeRange = `${formatTime(todayHours.openTime)}-${formatTime(
    todayHours.closeTime
  )}`;

  return timeRange;
}

/**
 * Format time string from 24-hour to 12-hour format with AM/PM
 * @param {string} timeString - Time in format "HH:MM:SS" or "HH:MM"
 * @returns {string} - Formatted time (e.g., "8:00 AM", "5:30 PM")
 */
export function formatTime(timeString) {
  if (!timeString) return "";

  const parts = timeString.split(":");
  let hour = parseInt(parts[0], 10);
  const minute = parts[1];

  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  hour = hour ? hour : 12; // Convert 0 to 12

  // Only show minutes if not :00
  const minuteDisplay = minute === "00" ? "" : `:${minute}`;

  return `${hour}${minuteDisplay}${ampm}`;
}

/**
 * Parse time string to minutes since midnight
 * @param {string} timeString - Time in format "HH:MM:SS" or "HH:MM"
 * @returns {number} - Minutes since midnight
 */
function parseTimeToMinutes(timeString) {
  if (!timeString || typeof timeString !== "string") {
    return 0;
  }
  const parts = timeString.split(":");
  if (parts.length < 2) {
    return 0;
  }
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (isNaN(hour) || isNaN(minute)) {
    return 0;
  }
  return hour * 60 + minute;
}

/**
 * Get status text and color for display
 * @param {Array} businessHours - Array of {day, openTime, closeTime} objects
 * @returns {Object} - {text, color} for display
 */
export function getOpenStatus(businessHours, timezone) {
  const isOpen = isCurrentlyOpen(businessHours, timezone);

  if (!businessHours || businessHours.length === 0) {
    return {
      text: "Hours not set",
      color: "gray",
      isOpen: false,
    };
  }

  const todayHours = getCurrentDayHours(businessHours, timezone);

  if (!todayHours) {
    return {
      text: "Closed today",
      color: "red",
      isOpen: false,
    };
  }

  if (isOpen) {
    return {
      text: "Open now",
      color: "green",
      isOpen: true,
    };
  }

  return {
    text: "Closed",
    color: "red",
    isOpen: false,
  };
}
